import axios, { type AxiosError } from 'axios';

import { api } from '@/services/api';

import type {
  CableColorRef,
  CableColorCreateInput,
  CableColorUpdateInput,
  CableLineCreateDto,
  CableLineDetails,
  CableWireCommutationPage,
  CableLineFilter,
  CableLineListResponse,
  CableLineGeometry,
  CableLineGeometryUpdateDto,
  CableLineMapRef,
  CableLineTypeRef,
  CableLineTypeDetails,
  CableLineTypeCreateInput,
  CableLineTypeUpdateInput,
  CableLineTypeAccessRights,
  EmployeeProfileRef,
  CableLineUpdateDto,
  CableLineRoute,
  CableLineRouteItem,
  CableLineRouteObjectCreateDto,
  CableLineRouteUpdateDto,
  CableLineWireAttenuationUpdateDto,
  CableLineWireColorUpdateDto,
  KmlImportResponse,
  KmlOperationStatus,
  NodeRef,
  TagRef,
  CableCatalogDetails,
  CableCatalogCreateInput,
  CableCatalogUpdateInput,
  CableCatalogListResponse,
  CableCatalogFilter,
} from '../types';

const defaultMap: CableLineMapRef = {
  mapId: null,
  name: 'Глобальная карта',
  type: 'geo',
  isDefault: true,
};

export interface CableDuctTransitItem {
  cableDuctId: number;
  name: string;
  position: number;
  isReverse: boolean;
}

const isNotFoundError = (error: unknown): error is AxiosError =>
  axios.isAxiosError(error) && error.response?.status === 404;

const buildQueryParams = (filter: CableLineFilter): string => {
  const params = new URLSearchParams();

  if (filter.typeId !== undefined) params.set('typeId', String(filter.typeId));
  if (filter.search) params.set('search', filter.search);
  if (filter.nodeId !== undefined) params.set('nodeId', String(filter.nodeId));
  if (filter.lengthFrom !== undefined) params.set('lengthFrom', String(filter.lengthFrom));
  if (filter.lengthTo !== undefined) params.set('lengthTo', String(filter.lengthTo));
  if (filter.isPlan !== undefined) params.set('isPlan', String(filter.isPlan));
  if (filter.tagIds?.length) {
    filter.tagIds.forEach((id) => params.append('tagIds', String(id)));
  }

  if (filter.page) params.set('page', String(filter.page));
  if (filter.limit) params.set('limit', String(filter.limit));
  if (filter.sortBy) params.set('sortBy', filter.sortBy);
  if (filter.sortDir) params.set('sortDir', filter.sortDir);

  return params.toString();
};

export const cableLinesApi = {
  list: async (filter: CableLineFilter = {}): Promise<CableLineListResponse> => {
    const query = buildQueryParams(filter);
    const response = await api.get<CableLineListResponse>(`/cable-lines${query ? `?${query}` : ''}`);
    return response.data;
  },

  details: async (id: number): Promise<CableLineDetails> => {
    const response = await api.get<CableLineDetails>(`/cable-lines/${id}`);
    return response.data;
  },

  wireCommutations: async (id: number, page: number, limit = 25): Promise<CableWireCommutationPage> => {
    const response = await api.get<CableWireCommutationPage>(`/cable-lines/${id}/wires/commutations?page=${page}&limit=${limit}`);
    return response.data;
  },

  ductTransit: {
    list: async (id: number): Promise<CableDuctTransitItem[]> => {
      const response = await api.get<{ items: CableDuctTransitItem[] }>(`/cable-lines/${id}/duct-transit`);
      return response.data.items;
    },
    replace: async (id: number, items: Array<Pick<CableDuctTransitItem, 'cableDuctId' | 'isReverse'>>): Promise<CableDuctTransitItem[]> => {
      const response = await api.put<{ items: CableDuctTransitItem[] }>(`/cable-lines/${id}/duct-transit`, { items });
      return response.data.items;
    },
  },

  types: async (): Promise<CableLineTypeRef[]> => {
    const response = await api.get<{ items: CableLineTypeRef[] }>('/cable-lines/types');
    return response.data.items;
  },

  typeDetails: async (id: number): Promise<CableLineTypeDetails> => {
    const response = await api.get<CableLineTypeDetails>(`/cable-lines/types/${id}`);
    return response.data;
  },

  createType: async (payload: CableLineTypeCreateInput): Promise<CableLineTypeDetails> => {
    const response = await api.post<CableLineTypeDetails>('/cable-lines/types', payload);
    return response.data;
  },

  updateType: async (id: number, payload: CableLineTypeUpdateInput): Promise<CableLineTypeDetails> => {
    const response = await api.put<CableLineTypeDetails>(`/cable-lines/types/${id}`, payload);
    return response.data;
  },

  deleteType: async (id: number): Promise<void> => {
    await api.delete(`/cable-lines/types/${id}`);
  },

  getTypeAccess: async (id: number): Promise<{
    accesses: CableLineTypeAccessRights[];
    profiles: EmployeeProfileRef[];
  }> => {
    const response = await api.get<{
      accesses: CableLineTypeAccessRights[];
      profiles: EmployeeProfileRef[];
    }>(`/cable-lines/types/${id}/access`);
    return response.data;
  },

  saveTypeAccess: async (id: number, accesses: CableLineTypeAccessRights[]): Promise<void> => {
    await api.put(`/cable-lines/types/${id}/access`, { accesses });
  },

  colors: async (): Promise<CableColorRef[]> => {
    const response = await api.get<{ items: CableColorRef[] }>('/cable-lines/colors');
    return response.data.items;
  },

  color: {
    get: async (id: number): Promise<CableColorRef> => {
      const response = await api.get<CableColorRef>(`/cable-lines/colors/${id}`);
      return response.data;
    },

    create: async (payload: CableColorCreateInput): Promise<CableColorRef> => {
      const response = await api.post<CableColorRef>('/cable-lines/colors', payload);
      return response.data;
    },

    update: async (id: number, payload: CableColorUpdateInput): Promise<CableColorRef> => {
      const url = `/cable-lines/colors/${id}`;
      if (import.meta.env.DEV) {
        console.log('[cableLinesApi.color.update] URL:', url, 'Payload:', payload);
      }
      const response = await api.put<CableColorRef>(url, payload);
      return response.data;
    },

    delete: async (id: number): Promise<void> => {
      await api.delete(`/cable-lines/colors/${id}`);
    },
  },

  tags: async (params: { search?: string; limit?: number } = {}): Promise<TagRef[]> => {
    const searchParams = new URLSearchParams();
    if (params.search) searchParams.set('search', params.search);
    if (params.limit) searchParams.set('limit', String(params.limit));

    const response = await api.get<{ items: TagRef[] }>(
      `/cable-lines/tags${searchParams.toString() ? `?${searchParams.toString()}` : ''}`,
    );
    return response.data.items;
  },

  nodes: async (params: { search: string; limit?: number }): Promise<NodeRef[]> => {
    const searchParams = new URLSearchParams();
    searchParams.set('search', params.search);
    if (params.limit) searchParams.set('limit', String(params.limit));

    const response = await api.get<{ items: NodeRef[] }>(`/cable-lines/nodes?${searchParams.toString()}`);
    return response.data.items;
  },

  create: async (payload: CableLineCreateDto): Promise<CableLineDetails> => {
    const response = await api.post<CableLineDetails>('/cable-lines', payload);
    return response.data;
  },

  update: async (id: number, payload: CableLineUpdateDto): Promise<CableLineDetails> => {
    const response = await api.put<CableLineDetails>(`/cable-lines/${id}`, payload);
    return response.data;
  },

  remove: async (id: number): Promise<void> => {
    await api.delete(`/cable-lines/${id}`);
  },

  exportBulkKml: async (ids: number[]): Promise<Blob> => {
    const response = await api.post<Blob>('/cable-lines/export/kml', { ids }, { responseType: 'blob' });
    return response.data;
  },

  updateWireColors: async (id: number, items: CableLineWireColorUpdateDto[]): Promise<void> => {
    await api.post(`/cable-lines/${id}/wires/colors`, { items });
  },

  updateWireAttenuation: async (id: number, items: CableLineWireAttenuationUpdateDto[]): Promise<void> => {
    await api.post(`/cable-lines/${id}/wires/attenuation`, { items });
  },

  maps: async (id: number): Promise<CableLineMapRef[]> => {
    try {
      const response = await api.get<{ items: CableLineMapRef[] }>(`/cable-lines/${id}/maps`);
      return response.data.items?.length ? response.data.items : [defaultMap];
    } catch (error) {
      if (isNotFoundError(error)) {
        return [defaultMap];
      }
      throw error;
    }
  },

  geometry: async (id: number, params: { mapId?: number } = {}): Promise<CableLineGeometry> => {
    const searchParams = new URLSearchParams();
    if (params.mapId !== undefined && params.mapId !== null) {
      searchParams.set('mapId', String(params.mapId));
    }
    try {
      const response = await api.get<CableLineGeometry>(
        `/cable-lines/${id}/geometry${searchParams.toString() ? `?${searchParams.toString()}` : ''}`,
      );
      return response.data;
    } catch (error) {
      if (isNotFoundError(error)) {
        return {
          lineId: id,
          mapId: params.mapId ?? null,
          geometry: {
            type: 'LineString',
            coordinates: [],
          },
          pointsCount: 0,
          updatedAt: new Date().toISOString(),
        };
      }
      throw error;
    }
  },
  endpoints: async (id: number, params: { mapId?: number } = {}) => {
    const searchParams = new URLSearchParams();
    if (params.mapId !== undefined && params.mapId !== null) {
      searchParams.set('mapId', String(params.mapId));
    }
    const response = await api.get(`/cable-lines/${id}/endpoints${searchParams.toString() ? `?${searchParams.toString()}` : ''}`);
    return response.data;
  },

  updateGeometry: async (
    id: number,
    payload: CableLineGeometryUpdateDto,
  ): Promise<CableLineGeometry> => {
    const response = await api.put<CableLineGeometry>(`/cable-lines/${id}/geometry`, payload);
    return response.data;
  },

  downloadKml: async (id: number, params: { mapId?: number } = {}): Promise<Blob> => {
    const searchParams = new URLSearchParams();
    if (params.mapId !== undefined && params.mapId !== null) {
      searchParams.set('mapId', String(params.mapId));
    }
    const response = await api.get<Blob>(
      `/cable-lines/${id}/kml${searchParams.toString() ? `?${searchParams.toString()}` : ''}`,
      { responseType: 'blob' },
    );
    return response.data;
  },

  importKml: async (
    id: number,
    payload: { file: File; koef?: number; mapId?: number | null },
  ): Promise<KmlImportResponse> => {
    const params = new URLSearchParams();
    if (payload.mapId !== undefined && payload.mapId !== null) {
      params.set('mapId', String(payload.mapId));
    }
    const formData = new FormData();
    formData.append('file', payload.file);
    formData.append('koef', String(payload.koef ?? 5));

    const response = await api.post<KmlImportResponse>(
      `/cable-lines/${id}/kml${params.toString() ? `?${params.toString()}` : ''}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  },

  route: async (id: number): Promise<CableLineRoute> => {
    const response = await api.get<CableLineRoute>(`/cable-lines/${id}/route`);
    return response.data;
  },

  updateRoute: async (id: number, payload: CableLineRouteUpdateDto): Promise<CableLineRoute> => {
    const response = await api.put<CableLineRoute>(`/cable-lines/${id}/route`, payload);
    return response.data;
  },

  addRouteObject: async (id: number, payload: CableLineRouteObjectCreateDto): Promise<CableLineRouteItem> => {
    const response = await api.post<CableLineRouteItem>(`/cable-lines/${id}/route-objects`, payload);
    return response.data;
  },

  removeRouteObject: async (id: number, nodeId: number): Promise<void> => {
    await api.delete(`/cable-lines/${id}/route-objects/${nodeId}`);
  },

  getKmlOperationStatus: async (operationId: string): Promise<KmlOperationStatus> => {
    const response = await api.get<KmlOperationStatus>(`/cable-lines/operations/${operationId}`);
    return response.data;
  },

  // Cable catalog methods
  catalog: {
    list: async (filter: CableCatalogFilter = {}): Promise<CableCatalogListResponse> => {
      const params = new URLSearchParams();
      if (filter.cableLineTypeId !== undefined && filter.cableLineTypeId !== null && filter.cableLineTypeId > 0) {
        params.set('cableLineTypeId', String(filter.cableLineTypeId));
      }
      if (filter.search && filter.search.trim()) {
        params.set('search', filter.search.trim());
      }
      if (filter.page !== undefined && filter.page > 0) {
        params.set('page', String(filter.page));
      }
      if (filter.limit !== undefined && filter.limit > 0) {
        params.set('limit', String(filter.limit));
      }
      const query = params.toString();
      const url = `/cable-lines/catalog${query ? `?${query}` : ''}`;
      console.log('[CableCatalog API] Request URL:', url);
      const response = await api.get<CableCatalogListResponse>(url);
      return response.data;
    },

    get: async (id: number): Promise<CableCatalogDetails> => {
      const response = await api.get<CableCatalogDetails>(`/cable-lines/catalog/${id}`);
      return response.data;
    },

    create: async (payload: CableCatalogCreateInput): Promise<{ id: number }> => {
      const response = await api.post<{ id: number }>('/cable-lines/catalog', payload);
      return response.data;
    },

    update: async (id: number, payload: CableCatalogUpdateInput): Promise<void> => {
      await api.put(`/cable-lines/catalog/${id}`, payload);
    },

    delete: async (id: number): Promise<void> => {
      await api.delete(`/cable-lines/catalog/${id}`);
    },

    getDependenciesCount: async (id: number): Promise<{ count: number }> => {
      const response = await api.get<{ count: number }>(`/cable-lines/catalog/${id}/dependencies`);
      return response.data;
    },
  },
};
